import React, { Suspense, useState } from 'react';

import {
  type OperationVariables,
  type TypedDocumentNode,
} from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import { XIcon } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

const CSVImporter = React.lazy(() =>
  import('csv-import-react').then(module => ({ default: module.CSVImporter })),
);

type CSVColumnDataType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'string[]'
  | 'number[]'
  | undefined;

export type ColumnType = {
  key: string;
  name: string;
  required: boolean;
  description?: string;
  suggested_mappings?: readonly string[];
  data_type?: CSVColumnDataType;
};

type FromCSVColumnDataType<T> = T extends 'string'
  ? string | undefined
  : T extends 'number'
  ? number | undefined
  : T extends 'boolean'
  ? boolean | undefined
  : T extends 'string[]'
  ? string[] | undefined
  : T extends 'number[]'
  ? number[] | undefined
  : never;

type GeneratedFormattedDataType<C extends ReadonlyArray<ColumnType>> = {
  [K in C[number] as K['key']]: FromCSVColumnDataType<K['data_type']>;
};

export type CSVImportModalProps<
  TVariables,
  TResult,
  C extends ReadonlyArray<ColumnType>,
> = {
  objectLabel: string;
  open: boolean;
  columns: C;
  onImportSuccess: () => void;
  onImporterClose: () => void;
  mutationQuery: TypedDocumentNode<TResult, TVariables>;
  constructMutationVariables: (
    formattedData: GeneratedFormattedDataType<C>[],
  ) => TVariables;
  renderSuccessContent?: (data: TResult) => React.ReactNode;
};

function transformValue(value: any, dataType: CSVColumnDataType) {
  if (value === 'null' || value === '') {
    return undefined;
  }

  switch (dataType) {
    case 'boolean':
      return ['true', 'TRUE'].includes(value);
    case 'number':
      return parseFloat(value);
    case 'string[]':
      return value.split(',');
    case 'number[]':
      return value.split(',').map((v: string) => parseFloat(v));
    case 'string':
    default:
      return value;
  }
}

export function CSVImportModal<
  TVariables extends OperationVariables,
  TResult,
  C extends ReadonlyArray<ColumnType>,
>(props: CSVImportModalProps<TVariables, TResult, C>) {
  const [showImporter, setShowImporter] = useState(true);
  const [insertData, { data, loading, error, reset }] = useMutation<
    TResult,
    TVariables
  >(props.mutationQuery);
  const { t } = useLocale();

  const handleImport = async (importData: {
    rows: { values: Record<string, any> }[];
  }) => {
    const formattedData = importData?.rows?.map(({ values }) => {
      const formattedListing = {} as GeneratedFormattedDataType<C>;

      for (const [key, value] of Object.entries(values)) {
        const column = props.columns.find(c => c.key === key);
        if (!column) {
          continue;
        }

        const keyOfType = key as keyof GeneratedFormattedDataType<C>;
        formattedListing[keyOfType] = transformValue(
          value,
          column.data_type,
        ) as GeneratedFormattedDataType<C>[typeof keyOfType];
      }

      return formattedListing;
    });

    setShowImporter(false);

    const variables = props.constructMutationVariables(formattedData);

    await insertData({
      variables,
      onCompleted: data => {
        if (data != null) {
          props.onImportSuccess();
        }
      },
    });
  };

  const handleClose = () => {
    props.onImporterClose();
    setShowImporter(true);
    reset();
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      maxWidth={showImporter ? 'lg' : 'md'}
      fullWidth
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <DialogTitle>
          {t('Import {{label}}', {
            label: props.objectLabel,
          })}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme => (theme.vars ?? theme).palette.grey[500],
          }}
        >
          <XIcon size={20} />
        </IconButton>
      </Box>
      {!showImporter && (
        <Stack
          spacing={2}
          sx={{
            p: 2,
          }}
        >
          {error && (
            <Alert severity="error">
              <pre>{JSON.stringify(error, null, 2)}</pre>
            </Alert>
          )}
          {error && (
            <Button
              variant="contained"
              onClick={() => {
                setShowImporter(true);
                reset();
              }}
            >
              {t('Try again')}
            </Button>
          )}
          {loading && (
            <Alert
              severity="info"
              icon={<CircularProgress size={18} disableShrink />}
            >
              <AlertTitle>{t('Importing')}</AlertTitle>
              {t('Importing data...')}
            </Alert>
          )}
          {data && (
            <>
              <Alert severity="success">{t('Import successful')}</Alert>
              {props.renderSuccessContent?.(data)}
            </>
          )}
        </Stack>
      )}
      {showImporter && (
        <Suspense fallback={<CircularProgress sx={{ m: 'auto' }} />}>
          <CSVImporter
            darkMode={false}
            onComplete={handleImport}
            isModal={false}
            template={{
              columns: props.columns,
            }}
          />
        </Suspense>
      )}
    </Dialog>
  );
}
